// CheckoutReturn.jsx — cinematic post-payment screen; polls Stripe status.
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Btn, Display, Eyebrow, Icon, Mark, Loader } from "./Primitives";

function Shell({ children }) {
  return (
    <main style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "100px 22px 60px", background: "radial-gradient(120% 90% at 50% 0%, var(--paper-2), var(--paper) 60%)" }}>
      <div style={{ width: "100%", maxWidth: 560, textAlign: "center" }}>{children}</div>
    </main>
  );
}

export default function CheckoutReturn({ sessionId, api, onPaid, onContinue, onShop }) {
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [order, setOrder] = useState(null);
  const paidRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${api}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("success");
          if (!paidRef.current) { paidRef.current = true; onPaid && onPaid(); }
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        if (attempts >= max) { setState("timeout"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= max) { setState("failed"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, api, onPaid]);

  if (state === "checking") {
    return (
      <Shell>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}><Mark size={40} /></div>
        <Display size="clamp(30px,5vw,46px)" style={{ fontWeight: 400 }}>Confirming your <span className="k-serif-i">payment</span></Display>
        <p style={{ color: "var(--ink-2)", marginTop: 14, fontSize: 15.5 }}>One moment while we make everything official.</p>
        <Loader label="Securing your order" />
      </Shell>
    );
  }

  if (state === "success") {
    const items = (order && order.items) || [];
    const total = items.reduce((s, it) => s + it.price * it.qty, 0);
    return (
      <Shell>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
          <span style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--sage)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 16px 34px -14px rgba(138,154,91,.6)" }}>
            <Icon name="check" weight="bold" size={30} color="#fff" />
          </span>
        </div>
        <Eyebrow>Order confirmed</Eyebrow>
        <Display size="clamp(34px,6vw,60px)" style={{ fontWeight: 400, margin: "14px 0 0" }}>Thank you. <span className="k-serif-i">Truly.</span></Display>
        <p style={{ color: "var(--ink-2)", marginTop: 14, fontSize: 16, lineHeight: 1.6, maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}>
          Your everyday care is on its way. A confirmation will follow shortly — quietly, of course.
        </p>
        {items.length > 0 && (
          <div style={{ textAlign: "left", margin: "30px auto 0", maxWidth: 380, border: "1px solid var(--line)", borderRadius: 12, padding: "18px 20px", background: "var(--paper-2)" }}>
            {items.map((it) => (
              <div key={it.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 14.5, color: "var(--ink)" }}>
                <span>{it.name} <span style={{ color: "var(--ink-3)" }}>× {it.qty}</span></span>
                <span style={{ fontFamily: "var(--font-display)" }}>${it.price * it.qty}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--line)", marginTop: 8, paddingTop: 12, fontWeight: 600 }}>
              <span>Total</span><span style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>${total}</span>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 30, flexWrap: "wrap" }}>
          <Btn variant="solid" size="lg" icon="arrow-right" onClick={onShop} testId="checkout-continue-btn">Continue shopping</Btn>
          <Btn variant="outline" size="lg" onClick={onContinue}>Back home</Btn>
        </div>
      </Shell>
    );
  }

  // failed | timeout
  return (
    <Shell>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
        <span style={{ width: 60, height: 60, borderRadius: "50%", background: "var(--sand-2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="warning" size={28} color="var(--ink-2)" />
        </span>
      </div>
      <Display size="clamp(30px,5vw,46px)" style={{ fontWeight: 400 }}>We couldn’t confirm it</Display>
      <p style={{ color: "var(--ink-2)", marginTop: 14, fontSize: 15.5, lineHeight: 1.6, maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}>
        {state === "timeout" ? "Your payment is taking a little longer than usual. If you were charged, you’ll receive a confirmation by email." : "Your payment wasn’t completed. Your bag is still saved — you can try again whenever you’re ready."}
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 30, flexWrap: "wrap" }}>
        <Btn variant="solid" size="lg" onClick={onShop} testId="checkout-retry-btn">Back to the range</Btn>
        <Btn variant="outline" size="lg" onClick={onContinue}>Back home</Btn>
      </div>
    </Shell>
  );
}
